'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import {
  bindWebMarketingAttributionIfNeeded,
  captureWebMarketingAttributionFromUrl,
  buildWebMarketingAttributionTouch,
  marketingEntryReferrer,
} from '@/lib/marketing/attribution';
import { captureAcquisition } from '@/lib/marketing/acquisition';
import { useAuthStore } from '@/stores/authStore';
import { initializeXPixel } from '@/lib/marketing/x-pixel';
import { flushXConversions } from '@/lib/marketing/x-conversions';

export function WebMarketingAttributionCapture() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  return <WebMarketingAttributionRuntime pathname={pathname} query={searchParams?.toString() ?? ''} />;
}

/** Shared runtime: Pages Router passes its own ready asPath; App Router uses navigation hooks. */
export function WebMarketingAttributionRuntime({ pathname, query }: { pathname: string | null; query: string }) {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const capturedDocumentReferrerRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !pathname || user?.isAdmin || pathname.startsWith('/admin')) return;

    const params = new URLSearchParams(query);
    const hash = window.location.hash || '';
    const landingPath = `${pathname}${params.toString() ? `?${params.toString()}` : ''}${hash}`;

    const entry = !capturedDocumentReferrerRef.current
      && (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type !== 'reload';
    const entryReferrer = entry ? marketingEntryReferrer(document.referrer) : null;
    capturedDocumentReferrerRef.current = true;
    captureWebMarketingAttributionFromUrl(params, landingPath, entryReferrer);
    initializeXPixel();
    flushXConversions();
    void captureAcquisition(isAuthenticated ? user?.id : undefined, {
      touch: buildWebMarketingAttributionTouch(params, pathname || '/', entryReferrer),
      referrer: entryReferrer, path: pathname || '/', entry,
    });
    if (isAuthenticated && user?.id && !user.isAdmin) {
      void bindWebMarketingAttributionIfNeeded(user.id);
    }
  }, [isAuthenticated, pathname, query, user?.id, user?.isAdmin]);

  useEffect(() => {
    const resume = () => {
      if (document.visibilityState !== 'visible' || window.location.pathname.startsWith('/admin')) return;
      const auth = useAuthStore.getState();
      if (!auth.user?.isAdmin) {
        initializeXPixel();
        flushXConversions();
        void captureAcquisition(auth.isAuthenticated ? auth.user?.id : undefined);
      }
    };
    // Extend active sessions without sending a request for every interaction.
    let lastInteraction = 0;
    const activity = () => {
      const now = Date.now();
      if (now - lastInteraction < 60000) return;
      lastInteraction = now;
      resume();
    };
    document.addEventListener('pointerdown', activity, { passive: true });
    document.addEventListener('keydown', activity);
    document.addEventListener('scroll', activity, { passive: true, capture: true });
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      document.removeEventListener('pointerdown', activity);
      document.removeEventListener('keydown', activity);
      document.removeEventListener('scroll', activity, true);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, []);

  return null;
}
